// Passage-set derivation for the official TestNav-style ELA flow.
//
// A "passage set" is a CONTIGUOUS run of questions that share the same
// (non-null) passage — the unit the printed/TestNav SHSAT frames as
// "ELA – PASSAGE SET X OF N". Standalone questions (no passage — e.g.
// Revising/Editing items, all of Math) belong to no set and keep the
// existing global "n OF total" header treatment.
//
// Pure helpers, shared by the legacy fixed-form player
// (Module/index.tsx). The adaptive player can't use these — it receives
// one slot at a time and the serve payload doesn't expose the module's
// full passage-group structure (see the passage-set notes in
// AdaptiveModule.tsx).

// The ELA "Revising/Editing Part B" sub-section marker (carried on the
// TestModuleQuestion.sectionDirections of the block's FIRST question). The
// stand-alone block runs from this marker through the END of the section —
// NOT a null-passage run: most Part B items carry their own single-question
// passage, so null-passage detection was wrong (it caught only the last
// item and split the rest into bogus "Passage Set of 1" screens).
const PART_B_MARKER = 'REVISING/EDITING PART B';
// The ELA "Revising/Editing Part A" marker (on the block's FIRST question).
// Part A is a passage-based sub-section (its questions share ONE passage the
// student reads), so it renders like a passage set — passage + within-group
// nav — but with its own "Revising/Editing Part A" label/count and is
// EXCLUDED from the reading "PASSAGE SET X OF N" numbering.
const PART_A_MARKER = 'REVISING/EDITING PART A';

interface QuestionLike {
    questionNum: number;
    passages?: { id: string } | null;
    sectionDirections?: string | null;
}

function isPartBMarker(marker?: string | null): boolean {
    return (marker ?? '').trim().toUpperCase() === PART_B_MARKER;
}

function isPartAMarker(marker?: string | null): boolean {
    return (marker ?? '').trim().toUpperCase() === PART_A_MARKER;
}

/** Index of the first question carrying the Part B marker, or -1 if the
 * module has no Part B marker. */
export function findPartBMarkerIndex(questions: QuestionLike[]): number {
    return questions.findIndex((q) => isPartBMarker(q.sectionDirections));
}

/** Index of the first Part A question, or -1 if the module has no Part A
 * block. */
export function findPartAStartIndex(questions: QuestionLike[]): number {
    return questions.findIndex((q) => isPartAMarker(q.sectionDirections));
}

/**
 * Structural fallback for tests with NO "REVISING/EDITING PART B" marker
 * (older ingests, e.g. "Digital SHSAT Practice Test 1"). Their trailing
 * revising/editing items are modeled as SINGLE-QUESTION passages (one passage
 * per question) and/or null-passage questions.
 *
 * Returns the start index of the maximal TRAILING run — at the END of the
 * section — of questions that are each either null-passage OR belong to a
 * single-question (size-1) contiguous passage group. Multi-question reading
 * passages (group size >= 2) are NOT part of it. Returns -1 when the last
 * question is a reading passage (no trailing stand-alone tail) so pure-reading
 * tests are unchanged. Only the trailing run is reclassified — a size-1
 * passage mid-section (before a reading passage) stays a normal passage set.
 */
function trailingStandaloneStartIndex(questions: QuestionLike[]): number {
    const n = questions.length;
    if (n === 0) return -1;
    // Contiguous same-passageId group size for each index (null-passage
    // questions are each their own singleton — size 1 — so they're eligible).
    const groupSize = new Array<number>(n).fill(1);
    let i = 0;
    while (i < n) {
        const pid = questions[i].passages?.id ?? null;
        if (pid == null) { i += 1; continue; }
        let j = i;
        while (j < n && (questions[j].passages?.id ?? null) === pid) j += 1;
        for (let k = i; k < j; k += 1) groupSize[k] = j - i;
        i = j;
    }
    const eligible = (idx: number): boolean =>
        (questions[idx].passages?.id ?? null) == null || groupSize[idx] === 1;
    if (!eligible(n - 1)) return -1;
    let start = n - 1;
    while (start - 1 >= 0 && eligible(start - 1)) start -= 1;
    return start;
}

/**
 * Unified ELA stand-alone block start index: the Part B MARKER when present
 * (marker-based behavior — unchanged for marker tests), else the structural
 * size<=1 trailing-run fallback. -1 = no stand-alone block.
 */
export function findStandaloneStartIndex(questions: QuestionLike[]): number {
    const markerIdx = findPartBMarkerIndex(questions);
    if (markerIdx !== -1) return markerIdx;
    return trailingStandaloneStartIndex(questions);
}

/** Index at which the reading-comprehension run ends — the earliest of the
 * Part A marker and the stand-alone block start (Part B marker OR the
 * structural trailing-run fallback). Reading passage sets are everything
 * before this; the section end when neither applies. */
function readingEndIndex(questions: QuestionLike[]): number {
    const markers = [findPartAStartIndex(questions), findStandaloneStartIndex(questions)]
        .filter((i) => i !== -1);
    return markers.length ? Math.min(...markers) : questions.length;
}

export interface PassageSet {
    passageId: string;
    questionNums: number[];
}

export interface PassageSetPosition {
    passageId: string;
    /** 1-based index of this set among the module's passage sets. */
    setIndex: number;
    /** Total number of passage sets in the module. */
    setCount: number;
    /** 1-based position of the question within its set. */
    positionInSet: number;
    /** Number of questions in this set. */
    setSize: number;
}

/** Group a module's ordered questions into contiguous READING passage sets.
 * Questions without a passage break the run and join no set.
 *
 * Both the Revising/Editing sub-sections are EXCLUDED, so the "PASSAGE SET
 * X OF N" total counts ONLY reading-comprehension passages: everything from
 * the earliest of the Part A / Part B markers onward is dropped. Part A
 * (its own shared passage) and Part B (stand-alone) get their own labeled
 * framing elsewhere. Mirrors the server's ``passageGroupsForModule``. */
export function computePassageSets(questions: QuestionLike[]): PassageSet[] {
    const reading = questions.slice(0, readingEndIndex(questions));
    const sets: PassageSet[] = [];
    let current: PassageSet | null = null;
    for (const question of reading) {
        const passageId = question.passages?.id ?? null;
        if (passageId == null) {
            current = null;
            continue;
        }
        if (current && current.passageId === passageId) {
            current.questionNums.push(question.questionNum);
        } else {
            current = { passageId, questionNums: [question.questionNum] };
            sets.push(current);
        }
    }
    return sets;
}

/** Locate a question inside the module's passage sets. Returns null for
 * standalone questions (no passage), which keep the global header. */
export function findPassageSet(
    sets: PassageSet[],
    questionNum: number,
): PassageSetPosition | null {
    for (let i = 0; i < sets.length; i++) {
        const position = sets[i].questionNums.indexOf(questionNum);
        if (position !== -1) {
            return {
                passageId: sets[i].passageId,
                setIndex: i + 1,
                setCount: sets.length,
                positionInSet: position + 1,
                setSize: sets[i].questionNums.length,
            };
        }
    }
    return null;
}

// ---------------------------------------------------------------------------
// Stand-alone items (the ELA "Revising/Editing Part B" block).
//
// The block is bounded by the ``REVISING/EDITING PART B`` marker: it runs
// from the marked question through the END of the section — ONE block,
// framed as "ELA – Stand alone items / N Questions / Start". Part B items
// commonly carry their own single-question passage, so the block is NOT a
// null-passage run; it's defined purely by the marker. Modules without the
// marker (older ingests, Math) have NO stand-alone block.
// ---------------------------------------------------------------------------

export interface StandaloneBlock {
    questionNums: number[];
}

export interface StandaloneBlockPosition {
    /** 1-based index of this block among the module's standalone blocks. */
    blockIndex: number;
    /** Total number of standalone blocks in the module. */
    blockCount: number;
    /** 1-based position of the question within its block. */
    positionInBlock: number;
    /** Number of questions in this block. */
    blockSize: number;
    /** First question number of this block — a stable per-block ack key. */
    firstQuestionNum: number;
}

/** The module's ONE unified "Stand alone" block = everything after the reading
 * passages through the end of the ELA section. Its start is ``readingEndIndex``
 * — the earliest of the Part A marker / Part B marker / the no-marker trailing
 * single-question run — so the block INCLUDES both Revising/Editing Part A
 * (shared passage) and Part B (single-passage / null). Returns an empty array
 * for a pure-reading module (no boundary). */
export function computeStandaloneBlocks(questions: QuestionLike[]): StandaloneBlock[] {
    const startIdx = readingEndIndex(questions);
    if (startIdx >= questions.length) return [];
    return [{ questionNums: questions.slice(startIdx).map((q) => q.questionNum) }];
}

/** Locate a question inside the module's standalone blocks. Returns null
 * for passage questions. */
export function findStandaloneBlock(
    blocks: StandaloneBlock[],
    questionNum: number,
): StandaloneBlockPosition | null {
    for (let i = 0; i < blocks.length; i++) {
        const position = blocks[i].questionNums.indexOf(questionNum);
        if (position !== -1) {
            return {
                blockIndex: i + 1,
                blockCount: blocks.length,
                positionInBlock: position + 1,
                blockSize: blocks[i].questionNums.length,
                firstQuestionNum: blocks[i].questionNums[0],
            };
        }
    }
    return null;
}

// Revising/Editing Part A is no longer framed separately — it is part of the
// unified "Stand alone" block above (its shared passage still renders and keeps
// within-group nav, but there is no separate Part A START/label). The Part A
// marker is still used for its directions screen and as one boundary of
// ``readingEndIndex``.
